import React from 'react';
import {stringParams} from 'utils';
import {connect} from 'react-redux';
import {users, customers} from '../ducks/index';

const MAX_LOAD_ATTEMPTS = 100;

/* Higher order component to simplify the process of fetching data from a duck and mapping it to your props.
 * This should be used in place of the standard redux connect() method.  It takes the same mapStateToProps and mapDispatchToProps parameters.
 *
 * This takes a map of DuckEntity objects, automatically loads them when the duckified component mounts, and passes their values in as a prop with the same name.
 * It also allows filtered ducks to easily fetch a subset of important objects.
 *
 * Examples:
 *
 * import tacos from '../ducks/tacos.js' // extends DuckEntity;
 * import burritos from '../ducks/burritos.js' // extends DuckEntity;
 *
 * class Menu extends React.Component(
 *   render(){
 *     const {tacos} = this.props;
 *
 *     if(tacos.pending || burritos.pending) {
 *       return <div>Waiting for food to load</div>;
 *     }
 *     else {
 *       return <div>There are {tacos.length} tacos and {burritos.length} on the menu!</div>
 *     }
 *   }
 * );
 *
 * duckify(() => { tacos, burritos }, mapStateToProps, mapDispatchToProps)(Menu);
 *
 *
 *
 * FILTERING:
 * Suppose we want to filter tacos for flour tortillas only:
 *
 * duckify(() => {
 *   tacos: filterDuck(tacos,
 *     {
 *        tortilla: 'flour' //generates URL query like '?tortilla=flour'
 *     },
 *     "flour_only_tacos" // optional state key
 *   ),
 *   burritos
 * )(Menu)
 *
 *
 * Suppose we want to filter based on a property from our duckified component, i.e.
 *
 * const DuckifiedMenu = duckify(
 *  (props) => ({
 *    tacos: filterDuck(tacos, {
 *        meat: ownProps.meat
 *      },
 *      `tacos_with_meat_filter_${ownProps.meat}` // OPTIONAL.  Will use stringified query if not provided.
 *    )
 *  })
 * )(Menu);
 *
 * ...
 *
 * <DuckifiedMenu meat="steak"/>
 *
 *
 *
 * SINGLE ITEMS:
 * Suppose your component needs a single specific item, not a collection:
 *
 * const DuckifiedMenu = duckify(
 *   (props) => ({
 *     tacos,
 *     burritos,
 *     selectedTaco: singleDuck(tacos, props.selectedTacoId)
 *   })
 * )(Menu)
 * ...
 * <DuckifiedMenu selectedTacoId="trashy_trailer_park"/>
 *
 *
 * @param mapDucksToProps    (ownProps) => {ducks}, where ducks are the ducks you want to fetch.  ownProps will be passed in with props from the parent component.
 * @param mapStateToProps    regular redux mapStateToProps method for mapping non-duck-related props.  This is called after duck mapping, so ownProps will have all ducks if you need them to map other things.
 * @param mapDispatchToProps regular redux mapDispatchToProps method if you need it.
 */

const duckify = (mapDucksToProps, mapStateToProps=()=>({}), mapDispatchToProps=()=>({})) => (WrappedComponent) =>
connect(
  (state, props) => {
    const {duckValues, duckMeta} = mapStateToDucks(state, mapDucksToProps(props, state));

    const combinedDuckMeta = {
      ...(props.duckMeta || {}),
      ...duckMeta
    };

    const allDucksReady = !Object.values(combinedDuckMeta).some((d) => !d.ready);

    const withDucks = {
      ...duckValues,
      duckMeta: combinedDuckMeta,
      allDucksReady
    };

    return {
      ...withDucks,
      ...mapStateToProps(state, {...props, ...withDucks})
    };
  },
  (dispatch, props) => ({
    ...mapDispatchToDucks(mapDucksToProps(props))(dispatch, props),
    ...mapDispatchToProps(dispatch, props)
  })
)(
  class Duckified extends React.Component {
    constructor(){
      super();

      this.state = {
        loadAttempts: 0,
        activeLoaders: []
      }
    }

    componentDidMount() {
      this.loadIfNeeded(this.props);
    }

    UNSAFE_componentWillReceiveProps(props){
      this.loadIfNeeded(props);
    }

    loadIfNeeded(props){
      if (this.state.loadAttempts > MAX_LOAD_ATTEMPTS) {
        throw new DuckOverflowException(WrappedComponent, this.state.activeLoaders);
      }

      const {duckLoaders, duckMeta} = props;
      let activeLoaders = [];

      Object.keys(duckLoaders).forEach((duckName) => {
        const loader = duckLoaders[duckName];

        if(duckMeta[duckName] && !duckMeta[duckName].ready && !duckMeta[duckName].error && !duckMeta[duckName].fetching){
          try {
            Promise.all([loader()]).catch((e) => {
              // DuckEntity sets error and errorMessage properties, then re-throws.
              // Generic catch here allows us to continue rendering other ducks while properly displaying error state from
              // the one that failed.

              console.warn(`loader for duck ${duckName} rejected a promise`, e);
            })
          }
          catch(e) {
            console.warn(`loader for duck ${duckName} threw an exception`, e);
          }

          activeLoaders = [...activeLoaders, duckName];
        }
      });

      if(activeLoaders.length) {
        this.setState({
          loadAttempts: this.state.loadAttempts+1,
          activeLoaders
        });
      }
      else {
        this.setState({
          loadAttempts: 0,
          activeLoaders
        });
      }
    }

    render(){
      return <WrappedComponent {...this.props}/>;
    }
  }
);

class DuckOverflowException {
  constructor(WrappedComponent, activeLoaders){
    this.value = {WrappedComponent, activeLoaders};
    this.message = 'Stack overflow detected loading DuckEntities';
    this.toString = function() {
      return `DuckoverflowException: ${message}: [${activeLoaders.join(', ')}]`;
    };
  }
}


/**
 * Helper method to represent a duck that fetches a filtered set of items, as opposed to all of them.
 *
 * @param duck     the DuckEntity object to fetch from
 * @param query    object representing the query parameters to use on the request.  i.e. {state: 'in_progress'} generates URL query string like ?state=in_progress
 * @param stateKey state key results should be stored under in redux.
 */
export const filterDuck = (duck, query, stateKey = undefined) => {
  const realStateKey = stateKey || stringParams(query);

  return {
    ...duck,
    actions: {
      ...duck.actions,
      load: () => duck.actions.load(query, realStateKey)
    },
    all: (state) => duck.all(state, realStateKey)
  };
};

/**
 * Helper method to represent a duck that fetches just a single item.
 *
 * @param duck  the DuckEntity we want to fetch from
 * @param id    the id of the object ot fetch, or an alias for it
 * @param opts  optional object to pass to the loader.  i.e. {form_type: 'application'}
 */
export const singleDuck = (duck, id, opts={}) => {
  return customDuck(duck, {
    load: () => duck.actions.get(id, {...opts}),
    read: (state) => duck.single(state, id)
  });
};

/**
 * Helper method to define a custom loadable duckifiable duck.
 * Provide a custom loader and accessor to dispatch whatever action and read whatever part of state, when needed
 *
 * @param duck
 * @param opts
 * @param opts.load   action creator to load the data this duck needs.  i.e. () => duck.actions.get(id)
 * @param opts.read   accessor to get the value for this duck from state.  i.e. (state) => duck.single(state, id)
 * @returns {{actions: {load: *}, all: *}}
 */
  //TODO : optionally rewrite filterDuck, singleDuck etc to use this?
export const customDuck = (duck, opts = {}) => ({
  ...duck,
  actions: {
    ...duck.actions,
    load: opts.load
  },
  all: opts.read
});

/**
 * Helper method to represent a duck that depends on another duck's result.
 * Parent duck will be fetched first, and once its result is ready, this duck will load.
 *
 * NOTE that the parent duck must be fetched elsewhere in the duck tree.  TODO TODO TODO
 *
 * For example, fetch a contact, then the associated user:
 *   const contact = singleDuck(contact, 'contact_id');
 *
 *   duckify({
 *     contact,
 *     user: dependentDuck(contact,
 *       ({value}) => singleDuck(user, value.user_id)
 *     )
 *   })
 *   ...
 *   contact {this.props.contact.id} is associated with user {this.props.user.id}
 *
 * @param dependsOn   the duck we're depending on
 * @param map         Maps the result from dependsOn to the duck we're interested in loading.  Params look like {value, meta}.  Should return a DuckEntity.
 * @returns {{actions: {load: function(): Function}, all: all}}
 */
export const dependentDuck = (dependsOn, map) => ({
  ...dependsOn,
  actions: {
    ...dependsOn.actions,
    load: () => (dispatch, getState) => {
      const dependsValue = dependsOn.all(getState());
      const dependsMeta = dependsValue.meta || readMeta(dependsValue);

      if (dependsMeta.ready) {
        const getLoader = map({
          value: dependsValue.value || dependsValue,
          meta: dependsMeta
        });

        return !!getLoader && dispatch(getLoader.actions.load());
      }
    }
  },
  all: (state) => {
    const dependsAll = dependsOn.all(state);
    const dependsValue = dependsAll.value;
    const dependsMeta = dependsAll.meta || readMeta(dependsAll);

    if (dependsValue === undefined || dependsValue === null) {
      return { value: null, meta: dependsMeta };
    }

    if (dependsMeta.ready) {
      const mapped = map({
        value: dependsValue.value || dependsValue,
        meta: dependsMeta
      });

      if (!mapped) {
        return {
          value: null,
          meta: dependsMeta
        }
      }
      else{
        return mapped.all(state);
      }
    } else {
      return {
        value: null,
        meta: dependsMeta
      };
    }
  }
});


/**
 * Loads a DuckEntityForm and pre-populates it with the value from a singleDuck
 *
 * NOTE: This is only for EDITING an existing item
 *
 * Note that you need to map the form properties into your component props separately using mapStateToProps.  i.e.
 *
 * const tacos = DuckEntity();
 * const tacoForm = DuckEntityForm();
 *
 * function mapDucksToProps (props) {
 *   const parentDuck = singleDuck(tacos, props.tacoId);
 *
 *   return: {
 *       parentDuck,
 *       formDuck: withForm(parentDuck, tacoForm)
 *   }
 * }
 *
 * @param entityDuck    The entity to be fetched, should be a singleDuck
 * @param formDuck      The form to be populated
 * @param transformLoad Optionally alter model when loading
 */
export const withForm = (entityDuck, formDuck, transformLoad = (model) => model) => {
  return ( dependentDuck(entityDuck, ({value, meta}) =>
    customDuck(formDuck, {
      load: () => (dispatch) => dispatch(formDuck.actions.load(transformLoad(value))), // loads the value into RRF
      read: (state) => ({
        value: state[formDuck.name],
        meta: {
          ...meta,
          // hold off on setting the 'ready' flag until the `formDuck.actions.load` call has finished.
          ready: meta.ready && (
            (state[formDuck.name].model.id && state[formDuck.name].model.id === value.id) ||
            (state[formDuck.name].model._id && state[formDuck.name].model._id === value._id)
          )
        }
      })
    })
  ))};

const readMeta = (all) => {
  const {
    ready=false,
    pending=true,
    fetching=false,
    error=false
  } = all;

  return {ready, pending, fetching, error}
};

const mapDispatchToDucks = (ducks) => (dispatch, ownProps) => {
  let duckLoaders = {};

  Object.keys(ducks).forEach((duckName) => {
    const duck = ducks[duckName];

    if(duck){
      duckLoaders[duckName] = () => dispatch(duck.actions.load());
    }
  });

  return {
    ...ownProps,
    dispatch,
    duckLoaders
  };
};

const mapStateToDucks = (state, ducks) => {
  let duckValues = {};
  let duckMeta = {};

  Object.keys(ducks).forEach((duckName) => {
    const duck = ducks[duckName];

    const all = duck ? duck.all(state) : {};

    if (duck === undefined) {
      // no-op
    } else if (all.hasOwnProperty('value') && all.hasOwnProperty('meta')){
      duckValues[duckName] = all.value;
      duckMeta[duckName] = all.meta;
    }
    else {
      duckValues[duckName] = all;
      duckMeta[duckName] = readMeta(all);
    }
  });

  return {duckValues, duckMeta};
};

export const currentUser = singleDuck(users, 'current');
export const currentCustomer = singleDuck(customers, 'current');

export {duckify as default, duckify};
